import { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { Settings } from 'lucide-react';
import { logout, getCurrentUsername, scheduleTokenRefresh, setupVisibilityRefresh, clearSessionAndRedirect } from '../services/api';
import { flushDraft } from '../services/outbox';
import { useToast, useRequestLock } from '../services/hooks';
import LoadingOverlay from './LoadingOverlay';
import MessageBar from './MessageBar';
import Modal from './Modal';

// Lazy-loaded — only fetched when the data source setup UI is actually opened.
// For users with a configured data source who never click the gear icon, this
// chunk (wizard + CSS) is never downloaded.
const DataSourceSetup = lazy(() => import('./OnboardingWizard'));

/**
 * Shared layout — header, message bar, user settings, DataSourceSetup.
 * Page content renders via ``<Outlet />``.
 *
 * Context passed to child pages via ``useOutletContext()``:
 *   runLocked, toast, isLocked, openDataSourceSetup
 */
function AppLayout() {
  const username = getCurrentUsername();
  const navigate = useNavigate();

  // ── User settings popup ─────────────────────────────────────────
  const [showUserSettings, setShowUserSettings] = useState(false);
  const [showDataSourceSetup, setShowDataSourceSetup] = useState(false);
  const [dsMode, setDsMode] = useState('settings');
  // 'onboarding' = first-time setup (non-dismissible, full-screen wizard)
  // 'settings'   = gear-icon settings (tabbed sheet, dismissible)

  // Bumped whenever the data source (provider/repo/token) changes so the
  // page remounts and reloads settings, tree, and content from scratch.
  const [dataSourceRevision, setDataSourceRevision] = useState(0);

  const [messages, toast, dismiss] = useToast();
  const { isLocked, runLocked } = useRequestLock();

  // ── Proactive token refresh ──────────────────────────────────
  // Single timer (~23h 55min) + visibility listener (tab return)
  useEffect(() => {
    if (!getCurrentUsername()) return; // skip if not logged in
    scheduleTokenRefresh();
    setupVisibilityRefresh();
  }, []);

  // ── Mobile keyboard avoidance (visualViewport API) ────────────

  useEffect(() => {
    const app = document.querySelector('.app');
    if (!app) return;

    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    if (!isTouchDevice) return;

    const setAppHeight = () => {
      const vv = window.visualViewport;
      if (!vv) return;
      app.style.setProperty('--app-height', `${vv.height}px`);
    };

    setAppHeight();
    window.visualViewport?.addEventListener('resize', setAppHeight);
    window.visualViewport?.addEventListener('scroll', setAppHeight);

    return () => {
      window.visualViewport?.removeEventListener('resize', setAppHeight);
      window.visualViewport?.removeEventListener('scroll', setAppHeight);
      app.style.removeProperty('--app-height');
    };
  }, []);

  // ── Outlet context ───────────────────────────────────────────

  const openDataSourceSetup = (mode = 'settings') => {
    setDsMode(mode);
    setShowDataSourceSetup(true);
  };

  const notifyDataSourceChanged = useCallback(() => {
    // Remount the page so settings, tree, and content reload as if it were a
    // first visit.  (The client no longer keeps a per-file content mirror —
    // all reads go straight through to the backend.)
    setDataSourceRevision((v) => v + 1);
  }, []);

  const outletContext = {
    runLocked,
    toast,
    isLocked,
    openDataSourceSetup,
  };

  // ── Session-expired confirm dialog ───────────────────────────
  // A 401 can happen on any route.  api.js dispatches `login-expired`;
  // we show a confirm box here so the open block editor can flush its
  // draft, and only clear tokens + redirect after the user confirms.
  const [sessionExpiredOpen, setSessionExpiredOpen] = useState(false);

  useEffect(() => {
    const onExpired = () => setSessionExpiredOpen(true);
    window.addEventListener('login-expired', onExpired);
    return () => window.removeEventListener('login-expired', onExpired);
  }, []);

  const handleSessionExpiredConfirm = async () => {
    setSessionExpiredOpen(false);
    // Final draft flush before wiping credentials — the editor has already
    // flushed on the `session-expiring` event; this awaits the last write.
    await flushDraft();
    // Preserve the cache: this is the same user re-authenticating, so their
    // in-progress edit survives.  Only explicit logout/switch wipes it.
    clearSessionAndRedirect({ wipeCache: false });
  };

  // ── Render ───────────────────────────────────────────────────

  return (
    <>
      <div className="app">
        {/* Top bar — app name placeholder */}
        <div className="topbar">
          <span className="topbar-title">JulyNote</span>
        </div>

        {/* Message bar — stacks below topbar, collapses when empty */}
        <MessageBar messages={messages} onDismiss={dismiss} />

        {/* Page content (Outlet) */}
        <Outlet key={dataSourceRevision} context={outletContext} />
      </div>

      <LoadingOverlay isLocked={isLocked} />

      {/* ── Session-expired confirm dialog ────────────────────────── */}
      <Modal
        show={sessionExpiredOpen}
        title="Session expired"
        onClose={() => setSessionExpiredOpen(false)}
        footer={
          <div style={{ display: 'flex', gap: 'var(--space-sm)', justifyContent: 'flex-end' }}>
            <button className="btn btn-secondary" onClick={() => setSessionExpiredOpen(false)}>
              Stay
            </button>
            <button className="btn btn-primary" onClick={handleSessionExpiredConfirm}>
              Sign in again
            </button>
          </div>
        }
      >
        <p style={{ margin: 0, color: 'var(--ink)' }}>
          Your session has expired. Any unsaved edit has been preserved — sign in again to continue.
        </p>
      </Modal>

      {/* ── User settings modal ─────────────────────────────────── */}
      <Modal
        show={showUserSettings}
        title="User settings"
        onClose={() => setShowUserSettings(false)}
        footer={
          <button
            className="btn"
            style={{ width: '100%' }}
            onClick={logout}
          >
            Logout
          </button>
        }
      >
        <div style={{ textAlign: 'center' }}>
          <button
            className="btn"
            style={{ width: '100%' }}
            onClick={() => {
              setShowUserSettings(false);
              setDsMode('settings');
              setShowDataSourceSetup(true);
            }}
          >
            <Settings size={14} style={{ marginRight: 'var(--space-xs)' }} />
            Data Source Setup
          </button>
        </div>
      </Modal>

      {/* ── Data Source Setup — lazy-loaded chunk ──────────────────── */}
      <Suspense fallback={null}>
        {showDataSourceSetup && (
          <DataSourceSetup
            mode={dsMode}
            onChanged={notifyDataSourceChanged}
            onDone={() => {
              setShowDataSourceSetup(false);
              setDsMode('settings');
              if (dsMode === 'onboarding') {
                window.location.reload();
              }
            }}
            onClose={() => setShowDataSourceSetup(false)}
          />
        )}
      </Suspense>
    </>
  );
}

export default AppLayout;
